import { message } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import { createClientLinksApi, createCompanyLinksApi, } from "@/components/CompanyLinks/ILinksApi";
import { b2bErrorCode, b2bErrorText, onB2BError } from "@/utils/b2bErrors";
/**
 * Стор общего компонента привязок (UC1115, UC1117; кабинет WP-DSP-3 создаёт свой экземпляр).
 * API приходит снаружи (`ILinksApi`); после любой записи список перечитывается из view — состояние
 * привязки, кто и когда заблокировал берутся только из данных (CCR-009).
 */
export class CompanyLinksStore {
    constructor(linkService = null) {
        this.linkService = linkService;
        this.links = [];
        this.stateFilter = null;
        this.isLoading = false;
        this.isSubmitting = false;
        this.readOnly = true;
        /** См. `LinksStoreOptions.canUnblockStaff` — по умолчанию персоналу разблокировка доступна. */
        this.canUnblockStaff = true;
        /** Режим отображения из ILinksApi; обычное наблюдаемое поле — вычисляемое от ненаблюдаемого api не обновлялось бы. */
        this.scope = "company";
        this.addError = null;
        /** Незавершённые заказы после последнего изменения срока (BR-LNK-11) — показываются информационно. */
        this.lastOpenOrders = [];
        this.api = null;
        this.generation = 0;
        makeAutoObservable(this, {
            api: false,
            generation: false,
            linkService: false,
        });
    }
    init(api, options) {
        this.api = api;
        this.scope = api.scope;
        this.generation += 1;
        this.readOnly = options.readOnly;
        this.canUnblockStaff = options.canUnblockStaff ?? true;
        this.links = [];
        this.stateFilter = null;
        this.addError = null;
        this.lastOpenOrders = [];
        void this.load();
    }
    /** Персонал, вкладка «Сотрудники» (UC1115). */
    initForCompany(companyId, options) {
        if (!this.linkService)
            throw new Error("CompanyLinksStore: сервис привязок не передан");
        this.init(createCompanyLinksApi(this.linkService, companyId), options);
    }
    /** Персонал, «Компании клиента» (UC1117). */
    initForClient(clientId, options) {
        if (!this.linkService)
            throw new Error("CompanyLinksStore: сервис привязок не передан");
        this.init(createClientLinksApi(this.linkService, clientId), options);
    }
    setReadOnly(readOnly) {
        this.readOnly = readOnly;
    }
    setStateFilter(state) {
        this.stateFilter = state;
        void this.load();
    }
    clearAddError() {
        this.addError = null;
    }
    clearOpenOrders() {
        this.lastOpenOrders = [];
    }
    async load() {
        const api = this.api;
        if (!api)
            return;
        const generation = this.generation;
        this.isLoading = true;
        try {
            const links = await api.list(this.stateFilter);
            runInAction(() => {
                if (generation === this.generation)
                    this.links = links;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить привязки сотрудников");
        }
        finally {
            runInAction(() => {
                if (generation === this.generation)
                    this.isLoading = false;
            });
        }
    }
    /** Добавление; ошибка остаётся у формы (`addError`), форма не очищается (AC 4, AC 6). */
    async add(input) {
        const api = this.api;
        if (!api)
            return false;
        this.isSubmitting = true;
        this.addError = null;
        try {
            const result = await api.add(input);
            message.success(result.state === "SCHEDULED"
                ? `Сотрудник ${result.clientName ?? result.phoneE164} привязан; привязка начнёт действовать в указанную дату`
                : `Сотрудник ${result.clientName ?? result.phoneE164} привязан`);
            await this.load();
            return true;
        }
        catch (error) {
            runInAction(() => {
                this.addError = {
                    code: b2bErrorCode(error),
                    text: b2bErrorText(error, "Не удалось добавить привязку — повторите попытку"),
                };
            });
            return false;
        }
        finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
    async update(linkId, patch, successText) {
        const api = this.api;
        if (!api)
            return false;
        this.isSubmitting = true;
        try {
            const result = await api.update(linkId, patch);
            runInAction(() => {
                this.lastOpenOrders = result.openOrders;
            });
            message.success(successText);
            await this.load();
            return true;
        }
        catch (error) {
            onB2BError(error, "Не удалось изменить привязку");
            return false;
        }
        finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
    /** «Отвязать» = `corp_link_update {endNow: true}` (§4.3). */
    end(linkId) {
        return this.update(linkId, { endNow: true }, "Сотрудник отвязан");
    }
    async setBlocked(linkId, blocked, reason) {
        const api = this.api;
        if (!api)
            return false;
        this.isSubmitting = true;
        try {
            await api.setBlocked(linkId, blocked, reason);
            message.success(blocked ? "Привязка заблокирована" : "Привязка разблокирована");
            await this.load();
            return true;
        }
        catch (error) {
            onB2BError(error, blocked ? "Не удалось заблокировать привязку" : "Не удалось разблокировать привязку");
            return false;
        }
        finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
    dispose() {
        this.api = null;
        this.scope = "company";
        // readOnly возвращается к «только просмотр»: до следующего init() один кадр может отрисоваться со старым режимом
        this.readOnly = true;
        this.canUnblockStaff = true;
        this.generation += 1;
        this.links = [];
        this.isLoading = false;
        this.isSubmitting = false;
        this.addError = null;
        this.lastOpenOrders = [];
    }
}
